import React, { useState, useEffect, useMemo } from 'react'
import { Result } from 'result'
import { History } from 'history'
import { TableLoader, ChartDataLoader } from 'uiComponents/loaders'
import { MessageKind } from 'uiComponents/messages'
import { delay } from 'utils'
import { useAccountChange } from 'reactUtils'
import { InlineDataTable, HeaderRow, TableHeader } from 'uiComponents/table'
import { TableToolbar } from 'uiComponents/table/tableToolbar'
import { NoResultsRow } from 'uiComponents/table/noResultsRow'
import { TimeSlotsServiceContext } from './context'
import { TimeSlotGroup, TimeSlotsValidationError } from './timeSlotsService'
import Infotip from 'uiComponents/infotip'
import Feature from 'features/feature'
import { SecondaryTabsRow } from 'uiComponents/navigation/secondaryTabsRow'
import { PageAddButton } from 'uiComponents/buttons/pageAddButton'
import './styles.scss'
import { withNavigation } from 'hocs'
import { Navigation } from 'navigation'
import { match as RouteMatch } from 'react-router-dom'
import { withFeatures } from 'features'
import { NestedContainerWrapper } from 'uiComponents/table/nestedContainer'
import InProductsTableHeader from 'products/tableHeaderSlot'
import {
    useGetAllProductsForSelect,
    useGetAllCategoriesForSelect,
    useIsLoadingCategoriesOrArticles,
} from 'products/redux'
import ComboboxFilter from 'uiComponents/combobox/comboboxFilter'
import { getFiltersFromUrl } from 'uiComponents/filter/filterHelpers'
import { useTranslation } from 'translations'
import { actionColumnWidth } from 'uiComponents/table/actionColumnWidth'
import { useCapacityAccess } from 'capacity/hooks/useCapacityAccess'
import { TimeSlotsListRedesign } from './listRedesign'
import { TimeSlotsList } from './listBody'

interface TimeSlotProductList {
    name: string
    id: string
}

export interface TimeSlotsListPageProps {
    history: History
    accountSlug: string
    navigation: Navigation
    match: RouteMatch<any>
    setActiveSection: (section: string, header: string) => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
    hasPermission: (permission: string, accountSlug: string) => boolean
    hasFeature: (feature: string, slug: string) => boolean
}

function LegacyTimeSlotsListPage(props: TimeSlotsListPageProps) {
    const { t } = useTranslation()
    const timeSlotsService = React.useContext(TimeSlotsServiceContext)
    const [loading, setLoading] = useState<boolean>(false)
    const [updating, setUpdating] = React.useState<boolean>(false)
    const [timeSlots, setTimeSlots] = useState<TimeSlotGroup[]>([])
    const [productsView, setProductsView] = useState<boolean>(props.navigation.query().timeslots_view === 'products')
    const [expandedElement, setExpandedElement] = useState<string>('')
    const [flattenedCategories, setFlattenedCategories] = useState<TimeSlotProductList[]>([])
    const productOptions = useGetAllProductsForSelect()
    const categoryOptions = useGetAllCategoriesForSelect()
    const isLoadingCategoriesOrArticles = useIsLoadingCategoriesOrArticles()

    const filterOptions = productsView ? productOptions : categoryOptions

    function getCategoryNames(allPls: TimeSlotProductList[], timeSlotGroup: TimeSlotGroup) {
        if (timeSlotGroup.productsLists.length === 0) {
            return t('timeSlots.list.defaultForAllCategories')
        }
        const names: string[] = []
        timeSlotGroup.productsLists.forEach((pl) => {
            names.push(pl.name)
        })
        return names.join(', ')
    }

    async function getTimeSlotsList() {
        const forArticles =
            props.hasFeature('TimeSlotsForProduct', props.accountSlug) &&
            props.navigation.query().timeslots_view === 'products'
        const data = await timeSlotsService.listTimeSlots(props.accountSlug, forArticles)
        const dataWithProductListNames =
            data?.map((timeSlotGroup: TimeSlotGroup) => {
                const productsListNames = getCategoryNames(timeSlotGroup.productsLists, timeSlotGroup)
                return { ...timeSlotGroup, productsListNames }
            }) || []
        const flattenedCategories =
            dataWithProductListNames.flatMap((timeSlotGroup) => timeSlotGroup.productsLists) || []

        setFlattenedCategories(flattenedCategories)
        setTimeSlots(dataWithProductListNames)
    }

    async function getData() {
        setLoading(true)
        try {
            await getTimeSlotsList()
            setLoading(false)
        } catch {
            props.replaceMessages('unknown_error', 'error', t('timeSlots.list.genericError'))
        }
    }

    useEffect(() => {
        const isProductsView = props.navigation.query().timeslots_view === 'products'
        setProductsView(isProductsView)
        getData()
        props.setActiveSection('timeSlots', 'Time Slots')
    }, [props.navigation.query().timeslots_view])

    useAccountChange(props.accountSlug, () => getData())

    const onPriorityUpdateConfirm = async (priority: string, id: string) => {
        try {
            setUpdating(true)
            const responseData = await timeSlotsService.updateTimeSlotGroupPriority(
                props.accountSlug,
                Number(priority),
                id,
            )
            handleResponse(responseData)
        } catch {
            setUpdating(false)
            props.replaceMessages('server_error', 'error', t('timeSlots.list.prioritySaveError'))
        }
    }

    function handleResponse(responseData: Result<TimeSlotGroup, TimeSlotsValidationError>) {
        responseData
            .ifFailure((err: TimeSlotsValidationError) => {
                setUpdating(false)
                props.replaceMessages(err.code, 'error', t('timeSlots.list.errorWithMessage', { message: err.message }))
            })
            .ifSuccess(() => {
                getTimeSlotsList()
                setUpdating(false)
                flashSuccessMessage()
            })
    }

    async function flashSuccessMessage() {
        props.replaceMessages('success', 'success', t('timeSlots.list.prioritySaveSuccess'))
        await delay(3000)
        props.removeAllMessages()
    }

    function onViewChange(view: 'categories' | 'products') {
        props.navigation.addQueryWithReplace({ timeslots_view: view, filter: undefined })
        setProductsView(view === 'products')
    }

    async function setExpandedTimeSlot(id: string) {
        expandedElement === id ? setExpandedElement('') : setExpandedElement(id)
    }

    const filteredTimeSlots = useMemo(() => {
        const selectedProductIds = getFiltersFromUrl('products')
            .split(',')
            .filter((id) => id)

        let filtered = timeSlots

        if (selectedProductIds.length > 0) {
            filtered = timeSlots.filter((timeSlot) => {
                if (productsView && timeSlot.article) {
                    return (
                        selectedProductIds.includes(timeSlot.article.id) ||
                        selectedProductIds.includes(timeSlot.article.uuid)
                    )
                }

                if (!productsView) {
                    if (timeSlot.productsLists.length === 0) {
                        return true
                    }
                    return timeSlot.productsLists.some((pl) => selectedProductIds.includes(pl.id))
                }

                return false
            })
        }

        return filtered.sort((a, b) => {
            const statusOrder = { active: 0, upcoming: 1, expired: 2 }
            const statusComparison = statusOrder[a.status] - statusOrder[b.status]
            if (statusComparison !== 0) {
                return statusComparison
            }

            const aPriority = a.priority ?? 0
            const bPriority = b.priority ?? 0
            return bPriority - aPriority
        })
    }, [timeSlots, productsView, props.navigation.query().filter])

    const viewSwitchDisabled = updating
    const newTimeSlotHref = `/account/${props.accountSlug}/products/time_slots/edit/new${location.search}`
    const timeSlotsForProductOn = props.hasFeature('TimeSlotsForProduct', props.accountSlug)

    const dropDownColumn = props.hasFeature('TimeSlotsForProduct', props.accountSlug) ? ['2.3em'] : []
    const columnWidths = [...dropDownColumn, '22em', null, '6em', '9.7em', '9.3em', actionColumnWidth(t('common.edit'))]

    return (
        <>
            <InProductsTableHeader>
                <SecondaryTabsRow
                    tabs={
                        timeSlotsForProductOn
                            ? (['categories', 'products'] as const).map((view) => ({
                                  name:
                                      view === 'products'
                                          ? t('timeSlots.list.tabs.products')
                                          : t('timeSlots.list.tabs.categories'),
                                  current: productsView === (view === 'products'),
                                  disabled: viewSwitchDisabled,
                                  disabledReason: t('timeSlots.list.priorityWait'),
                                  onClick: () => onViewChange(view),
                              }))
                            : []
                    }
                    actions={
                        props.hasPermission('partner_admin', props.accountSlug) && (
                            <div data-userpilot="timeslots-list-add-new-button">
                                <PageAddButton
                                    id="newTimeSlot"
                                    href={newTimeSlotHref}
                                    onClick={(event) => {
                                        if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
                                            event.preventDefault()
                                            props.history.push(newTimeSlotHref)
                                        }
                                    }}
                                >
                                    {t('timeSlots.list.addNew')}
                                </PageAddButton>
                            </div>
                        )
                    }
                />
            </InProductsTableHeader>
            <TableToolbar>
                <ComboboxFilter
                    className="time-slots-filter"
                    filterCategory="products"
                    options={filterOptions}
                    loading={isLoadingCategoriesOrArticles}
                    limitTags={10}
                    searchPlaceholder={
                        productsView ? t('timeSlots.list.filterByProduct') : t('timeSlots.list.filterByCategory')
                    }
                />
            </TableToolbar>
            <InlineDataTable columnWidths={columnWidths} bordered id="time-slots-list">
                <HeaderRow>
                    <Feature name="TimeSlotsForProduct" accountSlug={props.accountSlug}>
                        <TableHeader noPadding />
                    </Feature>
                    <TableHeader>{t('timeSlots.list.columns.name')}</TableHeader>
                    <TableHeader>
                        {productsView ? t('timeSlots.list.columns.product') : t('timeSlots.list.columns.categories')}
                    </TableHeader>
                    <TableHeader style={{ paddingLeft: '0.8em' }}>
                        {t('timeSlots.list.columns.priority')}
                        <Infotip pointer="right" maxWidth="25em">
                            {t('timeSlots.list.priorityTooltip')}
                        </Infotip>
                    </TableHeader>
                    <TableHeader>{t('timeSlots.list.columns.weeklyPattern')}</TableHeader>
                    <TableHeader>{t('timeSlots.list.columns.dateRange')}</TableHeader>
                    <TableHeader nonInteractive />
                </HeaderRow>
                {loading && <TableLoader />}
                {updating && <ChartDataLoader />}
                {!loading && (
                    <>
                        {filteredTimeSlots.length === 0 && (
                            <NoResultsRow
                                text={
                                    timeSlots.length === 0
                                        ? t('timeSlots.list.emptyState')
                                        : t('timeSlots.list.noFilterResults')
                                }
                            />
                        )}
                        {!productsView && filteredTimeSlots.length > 0 && (
                            <TimeSlotsList
                                accountSlug={props.accountSlug}
                                timeSlots={filteredTimeSlots}
                                flattenedCategories={flattenedCategories}
                                expandedElement={expandedElement}
                                productsView={productsView}
                                setExpandedTimeSlot={setExpandedTimeSlot}
                                onPriorityUpdateConfirm={onPriorityUpdateConfirm}
                                hasPermission={props.hasPermission}
                                hasFeature={props.hasFeature}
                            />
                        )}
                        {productsView && filteredTimeSlots.length > 0 && (
                            <NestedContainerWrapper flat grade={0} className="expanded">
                                <TimeSlotsList
                                    accountSlug={props.accountSlug}
                                    timeSlots={filteredTimeSlots}
                                    flattenedCategories={flattenedCategories}
                                    expandedElement={expandedElement}
                                    productsView={productsView}
                                    setExpandedTimeSlot={setExpandedTimeSlot}
                                    onPriorityUpdateConfirm={onPriorityUpdateConfirm}
                                    hasPermission={props.hasPermission}
                                    hasFeature={props.hasFeature}
                                />
                            </NestedContainerWrapper>
                        )}
                    </>
                )}
            </InlineDataTable>
        </>
    )
}

function TimeSlotsListPage(props: TimeSlotsListPageProps) {
    const redesign = useCapacityAccess(props.accountSlug)
    return redesign ? <TimeSlotsListRedesign {...props} /> : <LegacyTimeSlotsListPage {...props} />
}

export default withFeatures(withNavigation(TimeSlotsListPage))
